// Our own answer marks (not Kahoot's shapes): heart, plus, capsule, bolt for A–D; check and cross for true or false.
// Drawn on a 24-unit grid as solid shapes so they stay crisp from 16 to 96 px. Every mark carries its colour and its
// letter, so nothing relies on colour alone. The plus is never drawn red on white (a protected emblem): it is teal.
import type { Format } from '@shared/types';

/** The four answer marks, A–D. */
export type MarkKind = 'heart' | 'plus' | 'capsule' | 'bolt';
/** True or false marks. */
export type TfMarkKind = 'check' | 'cross';
/** Every glyph AnswerMark can draw. */
export type GlyphKind = MarkKind | TfMarkKind;

export interface MarkSpec<K extends GlyphKind = GlyphKind, L extends string = string> {
  kind: K;
  letter: L;
  /** Main colour (badge fill, ring). */
  colour: string;
  /** Pale tint for tile backgrounds. */
  soft: string;
  /** Darker shade for the tactile lip under a tile. */
  deep: string;
  /** Glyph and text colour on the main colour (navy on the amber bolt, white elsewhere). */
  ink: string;
  label: string;
}

export const MARKS: ReadonlyArray<MarkSpec<MarkKind, 'A' | 'B' | 'C' | 'D'>> = [
  { kind: 'heart', letter: 'A', colour: 'var(--mark-heart)', soft: 'var(--mark-heart-soft)', deep: 'var(--mark-heart-deep)', ink: '#FFFFFF', label: 'heart' },
  { kind: 'plus', letter: 'B', colour: 'var(--mark-plus)', soft: 'var(--mark-plus-soft)', deep: 'var(--mark-plus-deep)', ink: '#FFFFFF', label: 'plus' },
  { kind: 'capsule', letter: 'C', colour: 'var(--mark-capsule)', soft: 'var(--mark-capsule-soft)', deep: 'var(--mark-capsule-deep)', ink: '#FFFFFF', label: 'capsule' },
  { kind: 'bolt', letter: 'D', colour: 'var(--mark-bolt)', soft: 'var(--mark-bolt-soft)', deep: 'var(--mark-bolt-deep)', ink: 'var(--ink)', label: 'bolt' },
];

/** True (check, cobalt) and false (cross, orange): deliberately not green and red, which mean right and wrong. */
export const TF_MARKS: ReadonlyArray<MarkSpec<TfMarkKind, 'T' | 'F'>> = [
  { kind: 'check', letter: 'T', colour: 'var(--mark-true)', soft: 'var(--mark-true-soft)', deep: 'var(--mark-true-deep)', ink: '#FFFFFF', label: 'true' },
  { kind: 'cross', letter: 'F', colour: 'var(--mark-false)', soft: 'var(--mark-false-soft)', deep: 'var(--mark-false-deep)', ink: 'var(--ink)', label: 'false' },
];

const mod = (n: number, m: number) => ((n % m) + m) % m;
/** The A–D mark for an option position (wraps every four). */
export const markFor = (index: number): MarkSpec<MarkKind, 'A' | 'B' | 'C' | 'D'> => MARKS[mod(index, 4)];
/** The true or false mark for an option position (0 = true, 1 = false). */
export const tfMarkFor = (index: number): MarkSpec<TfMarkKind, 'T' | 'F'> => TF_MARKS[mod(index, 2)];
/** The mark players see for option `index` of a question in `format`. */
export const markForOption = (format: Format | undefined, index: number): MarkSpec => (format === 'tf' ? tfMarkFor(index) : markFor(index));
/** Look a mark up by kind. */
export const markByKind = (kind: GlyphKind): MarkSpec => [...MARKS, ...TF_MARKS].find((m) => m.kind === kind) ?? MARKS[0];

// ---------------------------------------------------------------------------
// Glyph geometry (24 × 24). Exported so the asset renderer and canvas share cards can draw the same shapes.
// ---------------------------------------------------------------------------
export const GLYPHS = {
  heart: 'M12 20.7c-.37 0-.73-.13-1.02-.37C7.14 17.18 2.6 13.4 2.6 8.78 2.6 5.83 4.9 3.5 7.74 3.5c1.73 0 3.22.86 4.26 2.25C13.04 4.36 14.53 3.5 16.26 3.5c2.84 0 5.14 2.33 5.14 5.28 0 4.62-4.54 8.4-8.38 11.55-.29.24-.65.37-1.02.37z',
  plus: 'M10.7 3.2H13.3A1.9 1.9 0 0 1 15.2 5.1V7.8A1 1 0 0 0 16.2 8.8H18.9A1.9 1.9 0 0 1 20.8 10.7V13.3A1.9 1.9 0 0 1 18.9 15.2H16.2A1 1 0 0 0 15.2 16.2V18.9A1.9 1.9 0 0 1 13.3 20.8H10.7A1.9 1.9 0 0 1 8.8 18.9V16.2A1 1 0 0 0 7.8 15.2H5.1A1.9 1.9 0 0 1 3.2 13.3V10.7A1.9 1.9 0 0 1 5.1 8.8H7.8A1 1 0 0 0 8.8 7.8V5.1A1.9 1.9 0 0 1 10.7 3.2Z',
  /** Two halves of a pill, laid flat; rotated −45° about the centre when drawn. The second half is drawn lighter. */
  capsuleA: 'M11.55 7.9H7.6A4.1 4.1 0 0 0 7.6 16.1H11.55Z',
  capsuleB: 'M12.45 7.9H16.4A4.1 4.1 0 0 1 16.4 16.1H12.45Z',
  bolt: 'M14.05 2.3 4.7 13.7h6.45l-1.7 8 9.85-11.95h-6.6l1.35-7.45z',
  check: 'M5.4 12.7 9.9 17.1 18.7 7.3',
  cross: 'M7.1 7.1 16.9 16.9M16.9 7.1 7.1 16.9',
} as const;

/** The glyph as SVG markup (no <svg> wrapper), in `fill`. Used by the static asset renderer. */
export function glyphMarkup(kind: GlyphKind, fill: string): string {
  switch (kind) {
    case 'heart': return `<path d="${GLYPHS.heart}" fill="${fill}"/>`;
    case 'plus': return `<path d="${GLYPHS.plus}" fill="${fill}"/>`;
    case 'capsule': return `<g transform="rotate(-45 12 12)"><path d="${GLYPHS.capsuleA}" fill="${fill}"/><path d="${GLYPHS.capsuleB}" fill="${fill}" fill-opacity="0.5"/></g>`;
    case 'bolt': return `<path d="${GLYPHS.bolt}" fill="${fill}" stroke="${fill}" stroke-width="1.1" stroke-linejoin="round"/>`;
    case 'check': return `<path d="${GLYPHS.check}" fill="none" stroke="${fill}" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"/>`;
    case 'cross': return `<path d="${GLYPHS.cross}" fill="none" stroke="${fill}" stroke-width="3.5" stroke-linecap="round"/>`;
  }
}

function Glyph({ kind }: { kind: GlyphKind }) {
  switch (kind) {
    case 'heart': return <path d={GLYPHS.heart} />;
    case 'plus': return <path d={GLYPHS.plus} />;
    case 'capsule': return <g transform="rotate(-45 12 12)"><path d={GLYPHS.capsuleA} /><path d={GLYPHS.capsuleB} fillOpacity={0.5} /></g>;
    case 'bolt': return <path d={GLYPHS.bolt} stroke="currentColor" strokeWidth={1.1} strokeLinejoin="round" />;
    case 'check': return <path d={GLYPHS.check} fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" strokeLinejoin="round" />;
    case 'cross': return <path d={GLYPHS.cross} fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round" />;
  }
}

/** A bare answer mark in currentColor. Decorative unless `title` is given. */
export function AnswerMark({ kind, size = 20, className = '', title }: { kind: GlyphKind; size?: number; className?: string; title?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} data-mark={kind}
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })} focusable="false">
      <Glyph kind={kind} />
    </svg>
  );
}

/**
 * The mark on its coloured badge (a rounded square) with the letter in a small tab at the corner.
 * size is the badge side in px; the glyph is drawn at about 56% of it.
 */
export function MarkBadge({ mark, size = 44, letter = true, className = '', title }: { mark: MarkSpec; size?: number; letter?: boolean; className?: string; title?: string }) {
  const glyph = Math.round(size * 0.56);
  const tab = Math.max(14, Math.round(size * 0.4));
  const showLetter = letter && mark.kind !== 'check' && mark.kind !== 'cross';
  return (
    <span className={`relative inline-flex shrink-0 items-center justify-center ${className}`} data-mark={mark.kind}
      style={{ width: size, height: size, borderRadius: Math.round(size * 0.3), background: mark.colour, color: mark.ink, boxShadow: `inset 0 -${Math.max(2, Math.round(size * 0.06))}px 0 rgb(0 0 0 / 0.14), inset 0 1px 0 rgb(255 255 255 / 0.22)` }}
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}>
      <AnswerMark kind={mark.kind} size={glyph} />
      {showLetter && (
        <span className="numeral absolute flex items-center justify-center bg-surface text-ink"
          style={{ width: tab, height: tab, right: -Math.round(tab * 0.28), bottom: -Math.round(tab * 0.28), borderRadius: Math.round(tab * 0.36), fontSize: Math.round(tab * 0.62), lineHeight: 1, boxShadow: `0 0 0 2px ${mark.colour}` }}>
          {mark.letter}
        </span>
      )}
    </span>
  );
}
